import { useCallback, useState } from 'react';
import type { ContentBlock } from '@beidou/agent-protocol';
import { copyBlocks, type CopyFormat } from '../utils/copyContent';

export type FeedbackRating = 'up' | 'down';

const DOWN_COMMENT_CHIPS = ['结果不准确', '不是我想要的类型', '推荐太慢', '内容重复'];
const ALL_COPY_FORMATS: CopyFormat[] = ['plain', 'markdown', 'json'];
const COPY_LABELS: Record<CopyFormat, string> = {
  plain: '纯文本',
  markdown: 'Markdown',
  json: 'JSON',
};

interface MessageFeedbackBarProps {
  apiUrl: string;
  token: string | null;
  messageId: string;
  traceId?: string;
  content: ContentBlock[];
  rating?: FeedbackRating;
  onRatingChange: (rating: FeedbackRating) => void;
  onToast?: (msg: string, type?: 'success' | 'error' | 'info') => void;
  showRegenerate?: boolean;
  onRegenerate?: () => void;
  regenerateDisabled?: boolean;
  copyFormats?: CopyFormat[];
}

function CopyIcon() {
  return (
    <svg className="feedback-icon" viewBox="0 0 16 16" width="13" height="13" aria-hidden focusable="false">
      <rect x="5.5" y="5.5" width="8" height="8" rx="1.25" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path d="M3.5 11V4.25A1.25 1.25 0 0 1 4.75 3H10" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg className="feedback-icon" viewBox="0 0 16 16" width="13" height="13" aria-hidden focusable="false">
      <path d="M3.5 8.5 6.5 11.5 12.5 4.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function MessageFeedbackBar({
  apiUrl,
  token,
  messageId,
  traceId,
  content,
  rating,
  onRatingChange,
  onToast,
  showRegenerate,
  onRegenerate,
  regenerateDisabled,
  copyFormats,
}: MessageFeedbackBarProps) {
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const [copyMenuOpen, setCopyMenuOpen] = useState(false);
  const [downPanelOpen, setDownPanelOpen] = useState(false);
  const resolvedCopyFormats = copyFormats && copyFormats.length > 0 ? copyFormats : ALL_COPY_FORMATS;

  const submitRating = useCallback(
    async (next: FeedbackRating, comment?: string) => {
      if (!token || submitting) return;
      if (rating === next && !comment) return;
      setSubmitting(true);
      try {
        const resp = await fetch(`${apiUrl}/v1/feedback`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            message_id: messageId,
            rating: next,
            comment,
            trace_id: traceId,
          }),
        });
        if (resp.ok) {
          onRatingChange(next);
          onToast?.(next === 'up' ? '感谢反馈' : '已记录，我们会改进', 'success');
          setDownPanelOpen(false);
          window.parent.postMessage(
            { channel: 'beidou-agent', type: 'feedback', rating: next, message_id: messageId },
            '*'
          );
        }
      } finally {
        setSubmitting(false);
      }
    },
    [apiUrl, token, messageId, traceId, rating, submitting, onRatingChange, onToast]
  );

  const onCopy = async (format: CopyFormat) => {
    setCopyMenuOpen(false);
    const ok = await copyBlocks(content, format);
    if (ok) {
      setCopyFailed(false);
      setCopied(true);
      onToast?.('已复制到剪贴板', 'success');
      setTimeout(() => setCopied(false), 1500);
    } else {
      setCopied(false);
      setCopyFailed(true);
      onToast?.('复制失败', 'error');
      setTimeout(() => setCopyFailed(false), 1500);
    }
  };

  return (
    <div className="message-feedback-wrap">
      <div className="message-feedback" role="toolbar" aria-label="消息反馈">
        <button
          type="button"
          className={`feedback-btn${rating === 'up' ? ' feedback-btn--active' : ''}`}
          onClick={() => submitRating('up')}
          disabled={!token || submitting}
          aria-label="有帮助"
          title="有帮助"
        >
          👍
        </button>
        <button
          type="button"
          className={`feedback-btn${rating === 'down' || downPanelOpen ? ' feedback-btn--active' : ''}`}
          onClick={() => setDownPanelOpen((v) => !v)}
          disabled={!token || submitting}
          aria-label="没帮助"
          title="没帮助"
          aria-expanded={downPanelOpen}
        >
          👎
        </button>
        {showRegenerate && onRegenerate && (
          <button
            type="button"
            className="feedback-btn feedback-btn--regenerate"
            disabled={regenerateDisabled}
            onClick={onRegenerate}
            aria-label="重新生成"
            title="重新生成"
          >
            ↻
          </button>
        )}
        {resolvedCopyFormats.length > 0 && (
          <div className="feedback-copy-wrap">
            <button
              type="button"
              className={`feedback-btn feedback-btn--copy${copied ? ' feedback-btn--copied' : ''}${copyFailed ? ' feedback-btn--copy-failed' : ''}`}
              onClick={() => setCopyMenuOpen((v) => !v)}
              aria-label="复制"
              title="复制"
              aria-expanded={copyMenuOpen}
            >
              {copied ? <CheckIcon /> : <CopyIcon />}
            </button>
            {copyMenuOpen && (
              <div className="copy-menu" role="menu">
                {resolvedCopyFormats.map((format) => (
                  <button key={format} type="button" role="menuitem" onClick={() => onCopy(format)}>
                    {COPY_LABELS[format]}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {downPanelOpen && (
        <div className="feedback-down-panel">
          <div className="feedback-down-label">哪里不满意？</div>
          <div className="feedback-down-chips">
            {DOWN_COMMENT_CHIPS.map((c) => (
              <button
                key={c}
                type="button"
                className="feedback-down-chip"
                onClick={() => submitRating('down', c)}
                disabled={submitting}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
